import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Alert, Divider, Form, Input, Select, Space} from 'antd';
import {useDispatch, useSelector} from 'react-redux';
import {InfoCircleOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {StyledBudgetRequestInfoSpace, StyledBudgetRequestInfoTitle} from '../BudgetRequestInfo/StyledBudgetRequestInfo';
import {
  StyledBudgetRequestDetails,
  StyledBudgetRequestDetailsHeader,
  StyledBudgetRequestDetailsHeaderTitle,
  StyledBudgetRequestDetailsHeaderAmount,
  StyledBudgetRequestDetailsVendorSpace,
  StyledBudgetRequestDetailsTable,
} from './StyledBudgetRequestDetails';
import AmountGhostInput from '../../../AmountGhostInput';
import VendorSelectorWithAuth from '../../TransactionsPage/VendorSelectorWithAuth';
import {amountHelpers, budgetHelpers, formHelpers, vendorsHelpers, textHelpers} from '../../../../utils/helpers';
import {vendorsActions} from '../../../../state/actions/vendors.actions';

const initialFormValues = {
  cost_centers: undefined,
  expense_category: undefined,
  limit_amount: 0,
  name: '',
  reason: '',
  procurement_type: undefined,
  vendor_id: undefined,
}

const {Item} = Form;

const formId = 'budget-details-form';

const BudgetRequestDetails = ({
  editable = true,
  onEdit,
  onlyReadView = false,

  ...rest
}) => {
  const [t] = useTranslation('main');
  const [vendor, setVendor] = useState(null);
  const dispatch = useDispatch();
  const [initialFormValuesState, setInitialFormValuesState] = useState(initialFormValues);

  const {budget, budgets, costCenterTags, expenseTags} = useSelector(state => ({
    budget: state?.budget?.budgetRequest,
    budgets: state.budget?.budgets || [],
    costCenterTags: state?.company?.costCenterTags || [],
    expenseTags: state?.company?.expenseTags || [],
  }));

  const [form] = Form.useForm();

  const isPurchaseOrder = budgetHelpers.isPurchaseOrderBudget(budget);

  const budgetRequestNumber = budgetHelpers.getBudgetNumber(budget);

  const invalidVendor = useMemo(() => vendorsHelpers.isVendorToBeValidated(vendor), [vendor]);

  const budgetOptions = useMemo(() => budgetHelpers.getBudgetOptions(budgets), [budgets]);

  const costCenterOptions = useMemo(() => formHelpers.getTagOptions(costCenterTags), [costCenterTags]);

  const expenseCategoryOptions = useMemo(() => formHelpers.getTagOptions(expenseTags), [expenseTags]);

  const procurementTypeOptions = useMemo(() => budgetHelpers.getProcurementTypeOptions(t), [t]);

  const loadVendorData = (budget) => {
    const id = budget?.vendor_id;
    if (!id) {
      if (vendor) setVendor(null);
      return;
    }

    dispatch(vendorsActions.getVendor({id, successCallback: setVendor}));
  }

  useEffect(() => {
    if (!budget) return;

    const formValues = {
      budget_id: budget?.budget_id,
      cost_centers: budget?.cost_centers?.[0] || undefined,
      expense_category: budget?.expense_category || undefined,
      limit_amount: textHelpers.roundToTwo(budget?.limit_amount || 0),
      name: budget?.name || '',
      procurement_type: budget?.procurement_type || undefined,
      reason: budget?.reason || '',
      vendor_id: budget?.vendor_id,
    }

    form.setFieldsValue(formValues);
    setInitialFormValuesState(formValues);
  }, [form, budget]);

  useEffect(() => {
    loadVendorData(budget);
  }, [budget]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitForm = () => form.submit();

  const requiredRules = [{required: true, message: t('validation.fieldIsRequired')}];

  const handlePressEnter = () => {
    handleSubmitForm();
    document.querySelectorAll(`#${formId} input select`).forEach((input) => input.blur());
  }

  const handleEditVendorSuccess = () => {
    if (!invalidVendor) return;

    loadVendorData(budget);
  }

  const formElementProps = {
    onBlur: handleSubmitForm,
    onPressEnter: handlePressEnter
  }

  const vendorField = (
    <StyledBudgetRequestDetailsVendorSpace align='center' size='small' wrap>
      {invalidVendor && <Alert message={`${t('add')} ${t('vat')}`} type='warning' showIcon />}
      <Item
        className={`invoice-vendor-select ${!editable ? 'padding-0' : ''}`}
        name='vendor_id'
      >
        <VendorSelectorWithAuth
          selectProps={{
            customSelectedRenderer: (props) =>
              vendorsHelpers.getVendorNameWithLink({
                vendor_id: props.value,
                name: props.name
              }),
            disabled: !editable,
            isEnableAdd: false,
            isEnableAddVat: invalidVendor,
            showArrow: false,
          }}
          onChange={handleSubmitForm}
          onSubmitSuccess={handleEditVendorSuccess}
          reloadOnMissingValue={invalidVendor}
          showBeneficiaryInfo={false}
        />
      </Item>
    </StyledBudgetRequestDetailsVendorSpace>
  );

  const amountField = editable ? (
    <AmountGhostInput
      className='amount-input'
      formItemProps={{rules: requiredRules}}
      inputProps={formElementProps}
      form={form}
      name='limit_amount'
    />
  ) : amountHelpers.getAmountWithCurrencyCode(budget?.limit_amount || 0);

  const getSelectField = (name, options = []) => {
    if (!editable) {
      const value = initialFormValuesState[name];
      return options.find(option => option.value === value)?.label || value;
    }
    return (
      <Item name={name}>
        <Select className='tags-select' options={options} onChange={handlePressEnter} popupMatchSelectWidth={false} />
      </Item>
    )
  }

  const nameField = editable ? (
    <Item name='name'>
      <Input prefix={budgetRequestNumber ? `${budgetRequestNumber} / ` : ''} {...formElementProps} />
    </Item>
  ) : budgetHelpers.getBudgetName(budget);

  const reasonField = editable ? (
    <Item className='w-100' name='reason'>
      <Input.TextArea autoSize={{minRows: 1}} {...formElementProps} />
    </Item>
  ) : form.getFieldValue('reason');

  const details = [
    {
      key: 'procurement_type',
      label: t('procurementType'),
      value: getSelectField('procurement_type', procurementTypeOptions),
    },
    {
      key: 'creation_date',
      label: t('creationDate'),
      value: budget?.created_at ? dayjs(budget?.created_at).format('DD MMM YYYY') : '',
    },
    {
      key: 'owner',
      label: t('Owner'),
      value: budgetHelpers.getOwner(budget?.reported_by)
    },
    isPurchaseOrder ? {
      key: 'vendor',
      label: t('vendor'),
      value: vendorField
    } : null,
    {
      key: 'budget',
      label: t('Budget'),
      value: editable ? getSelectField('budget_id', budgetOptions) : budgetHelpers.getBudgetLabel(budgets, budget?.budget_id),
    },
    {
      key: 'cost_center',
      label: t('costCenter'),
      value: getSelectField('cost_centers', costCenterOptions),
    },
    {
      key: 'expense_category',
      label: `${t('expense')} ${t('category')}`,
      value: getSelectField('expense_category', expenseCategoryOptions),
    },
    !onlyReadView && {
      key: 'status',
      label: t('status'),
      value: budgetHelpers.getBudgetStatusMark(budget)
    }
  ].filter(Boolean);

  const handleSubmit = (fields) => {
    const data = {
      ...fields,
      limit_amount: textHelpers.roundToTwo(parseFloat(fields.limit_amount || 0)),
    }
    onEdit?.({ data });
  };

  return (
    <StyledBudgetRequestDetails {...rest}>
      <Form
        id={formId}
        initialValues={initialFormValues}
        form={form}
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <Space size='large' direction='vertical' className='w-100'>
          <StyledBudgetRequestDetailsHeader>
            <StyledBudgetRequestDetailsHeaderTitle>
              {nameField}
            </StyledBudgetRequestDetailsHeaderTitle>
            <StyledBudgetRequestDetailsHeaderAmount>
              {amountField}
            </StyledBudgetRequestDetailsHeaderAmount>
          </StyledBudgetRequestDetailsHeader>
          <StyledBudgetRequestDetailsTable data={details} />
          <Divider />
          <StyledBudgetRequestInfoSpace size='small' direction='vertical'>
            <StyledBudgetRequestInfoTitle>
              {t('reason')} <InfoCircleOutlined />
            </StyledBudgetRequestInfoTitle>
            {reasonField}
          </StyledBudgetRequestInfoSpace>
        </Space>
      </Form>
    </StyledBudgetRequestDetails>
  );
}

BudgetRequestDetails.propTypes = {
  budget: PropTypes.object,
  editable: PropTypes.bool,
  onEdit: PropTypes.func,
}

export default BudgetRequestDetails;
